/**
 * token storage helpers — 集中管理 access/refresh token 的存取，避免在
 * middleware 链里散落 sessionStorage / localStorage 调用。
 *
 * 设计要点：
 *  - ACCESS_TOKEN_KEY = 'fp_access' → sessionStorage（短生命周期，关闭标签页即失效）
 *  - REFRESH_TOKEN_KEY = 'fp_refresh' → localStorage（用于刷新与重启后保持登录）
 *  - 提供 readAccess / readRefresh / writeTokens / clearTokens 四个纯函数，
 *    兼容 SSR/test 环境（typeof sessionStorage / localStorage 检查）。
 *  - 这些 key 仍然由 src/shared/api/client.ts re-export，保持业务侧
 *    `import { ACCESS_TOKEN_KEY } from '.../client'` 的兼容性。
 */

export const ACCESS_TOKEN_KEY = 'fp_access';
export const REFRESH_TOKEN_KEY = 'fp_refresh';
export const SESSION_EXPIRED_EVENT = 'fp:session-expired';

/**
 * 可替换的会话存储边界。
 * Web 默认使用 sessionStorage/localStorage；APK 启动时可注入一个由
 * Android Keystore / iOS Keychain 支撑的实现，业务 API 无需改动。
 */
export interface TokenStore {
  readAccess(): string | null;
  readRefresh(): string | null;
  write(access: string, refresh: string): void;
  clear(): void;
}

const webTokenStore: TokenStore = {
  readAccess: () => typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(ACCESS_TOKEN_KEY),
  readRefresh: () => typeof localStorage === 'undefined' ? null : localStorage.getItem(REFRESH_TOKEN_KEY),
  write: (access, refresh) => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(ACCESS_TOKEN_KEY, access);
    if (typeof localStorage !== 'undefined') localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },
  clear: () => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    if (typeof localStorage !== 'undefined') localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

let activeTokenStore: TokenStore = webTokenStore;

export function configureTokenStore(store: TokenStore): void {
  activeTokenStore = store;
}

export function readAccess(): string | null {
  return activeTokenStore.readAccess();
}

export function readRefresh(): string | null {
  return activeTokenStore.readRefresh();
}

export function writeTokens(access: string, refresh: string): void {
  activeTokenStore.write(access, refresh);
}

export function clearTokens(): void {
  activeTokenStore.clear();
}

/**
 * 广播一次“服务端已明确判定会话失效”。
 * 业务壳层可据此清理 React 状态和查询缓存；网络错误不能调用此函数。
 */
export function emitSessionExpired(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
}

export function redirectToLogin(): void {
  if (typeof window !== 'undefined' && typeof window.location?.assign === 'function') {
    window.location.assign('/login');
  }
}
